import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { moduleName } from './constants'
import { IChat, IChatsState } from './types'

import { ResponseWithPagination } from 'constants/types'
import { IChatListUpdate, IChatListUpdateTitle } from '../chatHistory/types'
import moment from 'moment'

const initialState: IChatsState = {
  type: null,
  search: null,
  chats: [],
  total: 0,
  isLoading: false,
  isLoaded: false,
  error: null
}

const slice = createSlice({
  name: moduleName,
  initialState,
  reducers: {
    // chat list actions
    loading(state: IChatsState) {
      state.isLoading = true
      state.error = null
    },
    finish(state: IChatsState) {
      state.isLoading = false
    },
    error(
      state: IChatsState,
      action: PayloadAction<{ error: IChatsState['error'] }>
    ) {
      const { error } = action.payload
      state.error = error
    },
    setChatsList(
      state: IChatsState,
      action: PayloadAction<
        ResponseWithPagination<IChat> & Pick<IChatsState, 'type' | 'search'>
      >
    ) {
      state.type = action.payload.type
      state.search = action.payload.search
      state.chats = action.payload.results
      state.total = action.payload.count
    },
    appendSingleChat(state: IChatsState, action: PayloadAction<IChat>) {
      state.chats = [action.payload, ...state.chats]
    },
    appendChatList(state: IChatsState, action: PayloadAction<IChat[]>) {
      state.chats = [...state.chats, ...action.payload]
    },
    updateSpecificChat(
      state: IChatsState,
      action: PayloadAction<Partial<IChat>>
    ) {
      const { uuid, ...chatDetails } = action.payload
      state.chats = state.chats.map((chat) =>
        chat.uuid === uuid ? { ...chat, ...chatDetails } : chat
      )
    },
    deleteChat(state: IChatsState, action: PayloadAction<string>) {
      const idx = state.chats.findIndex((chat) => chat.uuid === action.payload)
      state.chats.splice(idx, 1)
    },

    // unread messages actions
    resetUnreadMessagesForChat(
      state: IChatsState,
      action: PayloadAction<Partial<IChat>>
    ) {
      const { uuid } = action.payload
      state.chats = state.chats.map((chat) =>
        chat.uuid === uuid ? { ...chat, unread_messages_count: 0 } : chat
      )
    },

    // update chat list ordering and last message sent date
    updateChatStatusAndOrdering(
      state: IChatsState,
      action: PayloadAction<{
        uuid: string
        lastMessageSent: string
        unreadCount?: number
      }>
    ) {
      const {
        uuid: targetChatId,
        lastMessageSent: last_message_sent_at,
        unreadCount
      } = action.payload
      state.chats = state.chats
        .map((chat) =>
          chat.uuid === targetChatId
            ? {
                ...chat,
                unread_messages_count:
                  unreadCount ?? (chat.unread_messages_count ?? 0) + 1,
                last_message_sent_at
              }
            : chat
        )
        .sort(function (a, b) {
          return (
            moment(b.last_message_sent_at).valueOf() -
            moment(a.last_message_sent_at).valueOf()
          )
        })
    },

    // update chat details
    updateChatMembers(
      state: IChatsState,
      action: PayloadAction<IChatListUpdate>
    ) {
      const chat = action.payload
      state.chats = state.chats.map((p) =>
        p.uuid === chat.uuid
          ? {
              ...p,
              members_count: chat.members
            }
          : p
      )
    },
    deleteChatMembers(state: IChatsState, action: PayloadAction<string>) {
      const chat = action.payload
      state.chats = state.chats.map((p) =>
        p.uuid === chat
          ? {
              ...p,
              members_count: p.members_count - 1
            }
          : p
      )
    },
    updateChatTitle(
      state: IChatsState,
      action: PayloadAction<IChatListUpdateTitle>
    ) {
      const chat = action.payload
      state.chats = state.chats.map((p) =>
        p.uuid === chat.uuid
          ? {
              ...p,
              name: chat.title
            }
          : p
      )
    },
    reset(state: IChatsState) {
      Object.keys(initialState).forEach((key) => {
        state[key as string] = initialState[key]
      })
    }
  }
})

export default slice.reducer
export const {
  loading,
  finish,
  error,
  setChatsList,
  appendSingleChat,
  appendChatList,
  updateSpecificChat,
  deleteChat,
  reset,
  resetUnreadMessagesForChat,
  updateChatStatusAndOrdering,
  updateChatMembers,
  updateChatTitle,
  deleteChatMembers
} = slice.actions
